import React, { useMemo } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Flex, Text, Button, IconButton, Skeleton } from "@radix-ui/themes";
import {
  ChevronRightIcon,
  DotsVerticalIcon,
  Pencil1Icon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { useQuery, useQueryClient } from "react-query";
import {
  SampleForm,
  useSamplingApi,
} from "@/v2/entities/sampling/Sampling.api";
import { TargetRuleBuilder } from "@/v2/features/sampling/TargetStep";
import { DeleteSampleFormDialog } from "@/v2/features/sampling/DeleteSampleFormDialog";
import { toast } from "sonner";
import { QuestionItem } from "@/v2/features/sampling/QuestionItem";
import { useMediaQuery } from "react-responsive";
import { SampleConfiguration } from "@/v2/features/sampling/SampleConfiguration";
import styles from "@/v2/features/sampling/SampleForm.module.css";

interface ScorecardPageTopbarProps {
  form?: SampleForm;
  loading?: boolean;
}

function ScorecardPageTopbar({ form, loading }: ScorecardPageTopbarProps) {
  const navigate = useNavigate();

  return (
    <div
      className="w-full border-b px-8 py-4 flex items-center text-sm"
      style={{ 
        position: "sticky", 
        top: 0, 
        zIndex: 10,
        backgroundColor: "var(--backgroundDefault)",
        borderColor: "var(--gray-4)",
        color: "var(--gray-11)"
      }}
    >
      <Button variant="ghost" onClick={() => navigate("/quality/scorecards")}>
        Scorecards
      </Button>

      <ChevronRightIcon className="w-4 h-4 mx-2" style={{ color: "var(--gray-9)" }} />

      {loading ? (
        <Skeleton className="w-24 h-4" />
      ) : (
        <span style={{ fontWeight: 500, color: "var(--gray-12)" }}>
          {form?.name}
        </span>
      )}
    </div>
  );
}

function ScorecardPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams();
  const samplingApi = useSamplingApi();

  const isSmallScreen = useMediaQuery({ query: "(max-width: 1600px)" });

  const {
    data: form,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["sample-form", id],
    queryFn: () => samplingApi.getSampleForm(Number(id)),
    enabled: id != null,
    onError: () => {
      toast.error("Scorecard not found");
    },
  });

  const enabledConfigurations = useMemo(
    () => form?.sampleConfiguration?.filter((c) => c.isEnabled !== false) ?? [],
    [form?.sampleConfiguration]
  );

  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);

  const handleDeleteSuccess = React.useCallback(() => {
    queryClient.invalidateQueries(["sample-forms"]);
    navigate("/quality/scorecards");
  }, [queryClient, navigate]);

  if (isLoading) {
    return (
      <div style={{ width: "100%", minHeight: "100vh", background: "var(--backgroundDefault)" }}>
        <ScorecardPageTopbar loading />

        <div className="flex items-center justify-between px-8 py-8">
          <div className="flex items-center gap-4">
            <Skeleton className="w-[300px] h-[30px]" />
          </div>
        </div>

        <div className="px-8 pb-12" style={{ maxWidth: 1600 }}>
          <div
            style={{
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              gap: 64,
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Flex direction="column" gap="6">
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Text
                      size="1"
                      color="gray"
                      weight="medium"
                      style={{ textTransform: "capitalize" }}
                    >
                      Targeted Interactions
                    </Text>
                  </div>

                  <Skeleton className="w-[400px] h-[24px] mb-4" />
                  <Skeleton className="w-[280px] h-[20px]" />
                </div>
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Text
                      size="1"
                      color="gray"
                      weight="medium"
                      style={{ textTransform: "capitalize" }}
                    >
                      Validation Settings
                    </Text>
                  </div>
                  <Flex direction="column" gap="4">
                    <Flex direction="column" gap="2">
                      <Skeleton className="w-[400px] h-[24px] mb-4" />
                      <Skeleton className="w-[280px] h-[20px]" />
                    </Flex>
                  </Flex>
                </div>
              </Flex>
            </div>

            <div
              style={{
                flex: 1,
                minWidth: 0,
                background: "var(--gray-2)",
                padding: "24px",
                borderRadius: 12,
              }}
            >
              <div style={{ marginBottom: 24 }}>
                <Text
                  size="1"
                  color="gray"
                  weight="medium"
                  style={{ textTransform: "capitalize" }}
                >
                  Scorecard Preview
                </Text>
              </div>
              <Flex direction="column" gap="3">
                <Flex direction="column">
                  <Skeleton className="w-[100px] h-[16px] my-[2px]" />
                  <Skeleton className="w-[160px] h-[16px] my-[2px]" />
                </Flex>

                <Flex direction="column" gap={"10px"}>
                  <Skeleton className="w-[80px] h-[16px] my-[2px]" />
                  <Skeleton className="w-[100px] h-[16px] my-[2px]" />
                  <Skeleton className="w-[80px] h-[16px] my-[2px]" />
                </Flex>
              </Flex>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || form == null) {
    return <Navigate to="/quality/scorecards" />;
  }

  return (
    <>
      <DeleteSampleFormDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onSuccess={handleDeleteSuccess}
        formId={form?.id}
      />

      <div style={{ width: "100%", minHeight: "100vh", background: "var(--backgroundDefault)" }}>
        <ScorecardPageTopbar form={form} />

        <div className="flex items-center justify-between px-8 py-8">
          <div className="flex items-center gap-4">
            <Text as="div" size="6" weight="bold" color="gray" highContrast>
              {form.name}
            </Text>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              color="gray"
              highContrast
              onClick={() => navigate(`/quality/scorecards/${id}/edit`)}
            >
              <Pencil1Icon style={{ marginRight: 8 }} />
              Edit
            </Button>

            <DropdownMenu
              align="end"
              items={[
                {
                  label: "Delete",
                  onClick: () => setShowDeleteDialog(true),
                  icon: <TrashIcon />,
                },
              ]}
            >
              <DropdownMenu.Trigger>
                <IconButton variant="soft" color="gray">
                  <DotsVerticalIcon />
                </IconButton>
              </DropdownMenu.Trigger>
            </DropdownMenu>
          </div>
        </div>

        <div className="px-8 pb-12" style={{ maxWidth: 1600 }}>
          <div
            style={{
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              gap: 64,
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Flex direction="column" gap="6">
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Text size="2" color="gray" weight="medium">
                      Targeted Interactions
                    </Text>
                  </div>

                  <Flex
                    direction={"column"}
                    gap={"2"}
                    className={styles.targetStep}
                  >
                    <Text size={"3"} color={"gray"}>
                      This scorecard will be used for
                      <Button
                        variant="soft"
                        color="gray"
                        highContrast
                        style={{
                          marginTop: -4,
                          marginLeft: 4,
                          fontSize: 16,
                          fontWeight: 500,
                          pointerEvents: "none",
                        }}
                      >
                        {form.qaRules === null
                          ? "All Interactions"
                          : "Custom Interactions"}
                      </Button>
                    </Text>

                    {form.qaRules !== null && (
                      <TargetRuleBuilder
                        value={form.qaRules}
                        viewOnly
                        onChange={() => {}}
                      />
                    )}
                  </Flex>
                </div>
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Text size="2" color="gray" weight="medium">
                      Validation Settings
                    </Text>
                  </div>
                  <Flex
                    direction="column"
                    gap="4"
                    minWidth={isSmallScreen ? "587px" : undefined}
                  >
                    {!enabledConfigurations.length && (
                      <Text size="2" color="gray" >
                        No enabled validation sets.
                      </Text>
                    )}
                    {enabledConfigurations.map((c, idx) => {
                      return (
                        <SampleConfiguration
                          key={idx}
                          value={{
                            numberOfTickets: c.numberOfTickets ?? undefined,
                            daysUntilNextRun: c.daysUntilNextRun ?? undefined,
                            qaRules: c.qaRules ?? undefined,
                            sampleFormId: form.id,
                            isEnabled: c.isEnabled ?? undefined,
                            id: c.id,
                            sources: c.sources ?? ["tickets"],
                          }}
                          onChange={() => {}}
                          viewOnly
                        />
                      );
                    })}
                  </Flex>
                </div>
              </Flex>
            </div>

            <Flex direction={"column"} gap={"2"} style={{}}>
              <Text size="2" color="gray" weight="medium">
                Scorecard Preview
              </Text>

              <Flex
                p={"2"}
                direction="column"
                gap="4"
                style={{
                  background: "var(--gray-2)",
                  borderRadius: 16,
                }}
              >
                {form.questions?.length && form.questions.length > 0 ? (
                  form.questions.map((value) => (
                    <QuestionItem
                      viewOnly
                      key={value.key}
                      value={{
                        key: value.key,
                        title: value.title,
                        content: value.content,
                        type: value.type,
                        brief: value.brief ?? undefined,
                      }}
                      isEditing={false}
                      allTitles={[]}
                      onSave={() => {}}
                      onDiscard={() => {}}
                      onDelete={() => {}}
                      onStartEditing={() => {}}
                    />
                  ))
                ) : (
                  <Text size="2" color="gray">
                    No questions defined for this QA score.
                  </Text>
                )}
              </Flex>
            </Flex>
          </div>
        </div>
      </div>
    </>
  );
}

export default ScorecardPage;
